/**
 * Animated SVG energy-flow layer for the hybrid system canvas.
 * Flow opacity and animation speed respond to current simulated kW values.
 * @module HybridSystemIntegration/components/FlowAnimationLayer
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CANVAS_NODE_SIZE, CANVAS_SIZE, COMPONENT_TYPES } from '../constants';
import { roundMetric } from '../utils';
import styles from '../HybridSystemIntegration.module.css';

/**
 * Get the center point of a canvas node.
 * @param {Object} node - Canvas node.
 * @returns {Object|null} Center coordinates.
 */
function getCenter(node) {
  if (!node) {
    return null;
  }

  return {
    x: node.x + (CANVAS_NODE_SIZE.width / 2),
    y: node.y + (CANVAS_NODE_SIZE.height / 2),
  };
}

/**
 * Build a smooth SVG path between two points.
 * @param {Object} start - Start point.
 * @param {Object} end - End point.
 * @returns {string} SVG path data.
 */
function buildPath(start, end) {
  const middleX = (start.x + end.x) / 2;
  return `M ${start.x} ${start.y} C ${middleX} ${start.y}, ${middleX} ${end.y}, ${end.x} ${end.y}`;
}

/**
 * Render an active animated flow path.
 * @param {Object} props - Component props.
 * @returns {JSX.Element|null} Flow path with label.
 */
function FlowPath({ start, end, amount, kind, label }) {
  if (!start || !end || amount <= 0.02) {
    return null;
  }

  const duration = Math.max(0.85, 5.4 / Math.max(amount, 0.3));
  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };

  return (
    <g className={styles.flowGroup}>
      <path
        d={buildPath(start, end)}
        className={`${styles.flowBase} ${styles[`${kind}FlowBase`]}`}
      />
      <path
        d={buildPath(start, end)}
        className={`${styles.flowPath} ${styles[`${kind}Flow`]}`}
        style={{ animationDuration: `${duration}s` }}
      />
      <circle className={`${styles.flowParticle} ${styles[`${kind}Particle`]}`} r="5">
        <animateMotion dur={`${duration}s`} repeatCount="indefinite" path={buildPath(start, end)} />
      </circle>
      <text x={midpoint.x} y={midpoint.y - 10} className={styles.flowLabel}>
        {label}: {roundMetric(amount, 1)} kW
      </text>
    </g>
  );
}

/**
 * Animated energy-flow overlay for heat, electricity, gas, and ambient energy.
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.nodes - Canvas nodes.
 * @param {Object} props.result - Simulation result snapshot.
 * @returns {JSX.Element} SVG flow layer.
 */
export function FlowAnimationLayer({ nodes, result }) {
  const { t } = useTranslation();
  const heatPump = nodes.find((node) => [
    COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP,
    COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP,
  ].includes(node.type));
  const boiler = nodes.find((node) => node.type === COMPONENT_TYPES.GAS_BOILER);
  const pv = nodes.find((node) => node.type === COMPONENT_TYPES.SOLAR_PV);
  const storage = nodes.find((node) => node.type === COMPONENT_TYPES.THERMAL_STORAGE);
  const load = nodes.find((node) => node.type === COMPONENT_TYPES.BUILDING_LOAD);
  const controller = nodes.find((node) => node.type === COMPONENT_TYPES.SMART_CONTROLLER);
  const heatPumpCenter = getCenter(heatPump);
  const boilerCenter = getCenter(boiler);
  const pvCenter = getCenter(pv);
  const storageCenter = getCenter(storage);
  const loadCenter = getCenter(load);
  const controllerCenter = getCenter(controller);
  const ambientStart = heatPumpCenter ? { x: 28, y: heatPumpCenter.y - 38 } : null;

  return (
    <svg
      className={styles.flowSvg}
      viewBox={`0 0 ${CANVAS_SIZE.width} ${CANVAS_SIZE.height}`}
      role="img"
      aria-label={t('hybridSystemTool.canvas.flowAria')}
    >
      <FlowPath
        start={ambientStart}
        end={heatPumpCenter}
        amount={result.heatPumpHeatKw * Math.max(0, 1 - 1 / Math.max(result.cop, 1))}
        kind="ambient"
        label={t('hybridSystemTool.energy.ambient')}
      />
      <FlowPath
        start={pvCenter}
        end={heatPumpCenter}
        amount={result.pvToHeatPumpKw}
        kind="electric"
        label={t('hybridSystemTool.energy.electric')}
      />
      <FlowPath
        start={heatPumpCenter}
        end={loadCenter}
        amount={result.heatPumpHeatKw}
        kind="thermal"
        label={t('hybridSystemTool.energy.heatPumpHeat')}
      />
      <FlowPath
        start={boilerCenter}
        end={loadCenter}
        amount={result.boilerHeatKw}
        kind="fossil"
        label={t('hybridSystemTool.energy.boilerHeat')}
      />
      <FlowPath
        start={storageCenter}
        end={loadCenter}
        amount={result.storageDischargeKw}
        kind="storage"
        label={t('hybridSystemTool.energy.storageHeat')}
      />
      <FlowPath
        start={controllerCenter}
        end={heatPumpCenter}
        amount={result.heatPumpHeatKw > 0 ? 0.5 : 0}
        kind="control"
        label={t('hybridSystemTool.energy.control')}
      />
    </svg>
  );
}

export default FlowAnimationLayer;
